import * as pako from 'pako';
import * as THREE from 'three';

import { LruCache } from '../../shared/lru-cache.js';

export interface QuantizedMeshHeader {
  centerX: number;
  centerY: number;
  centerZ: number;
  minimumHeight: number;
  maximumHeight: number;
  boundingSphereCenterX: number;
  boundingSphereCenterY: number;
  boundingSphereCenterZ: number;
  boundingSphereRadius: number;
  horizonOcclusionPointX: number;
  horizonOcclusionPointY: number;
  horizonOcclusionPointZ: number;
}

export interface QuantizedMeshData {
  header: QuantizedMeshHeader;
  vertexCount: number;
  u: Uint16Array;
  v: Uint16Array;
  height: Uint16Array;
  indices: Uint32Array;
  triangleCount: number;
  edges: {
    west: number[];
    south: number[];
    east: number[];
    north: number[];
  };
}

export interface GeoBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export const TERRAIN_MESH_CACHE_ENTRIES = 500;

/**
 * Loader for Cesium Quantized Mesh terrain tiles
 * Spec: https://github.com/CesiumGS/quantized-mesh
 */
export class QuantizedMeshLoader {
  cache: LruCache<string, QuantizedMeshData>;
  private readonly rawCache = new LruCache<string, ArrayBuffer>(TERRAIN_MESH_CACHE_ENTRIES);

  constructor() {
    this.cache = new LruCache(TERRAIN_MESH_CACHE_ENTRIES);
  }

  /**
   * Return owned bytes that can be transferred to a worker without detaching
   * the cached tile. Cancellation is checked even for cache hits.
   */
  async loadBuffer(url: string, signal?: AbortSignal): Promise<ArrayBuffer | null> {
    signal?.throwIfAborted();
    const cached = this.rawCache.get(url);
    if (cached) return cached.slice(0);

    const response = await fetch(url, { signal });
    signal?.throwIfAborted();
    if (response.status === 403) return null;
    if (!response.ok) {
      throw new Error(`Failed to fetch terrain tile: ${response.status} ${response.statusText}`);
    }

    const buffer = await response.arrayBuffer();
    signal?.throwIfAborted();
    this.rawCache.set(url, buffer);
    return buffer.slice(0);
  }

  /**
   * Load and parse a quantized mesh terrain tile.
   * AbortErrors are silently re-thrown so callers can distinguish cancellation.
   */
  async load(url: string, signal?: AbortSignal): Promise<QuantizedMeshData | null> {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    // Check cache
    if (this.cache.has(url)) {
      return this.cache.get(url) ?? null;
    }

    try {
      const arrayBuffer = await this.loadBuffer(url, signal);
      if (arrayBuffer === null) return null;
      const data = this.parse(arrayBuffer);

      // Cache the result
      this.cache.set(url, data);

      return data;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }
      console.error('Error loading quantized mesh:', url, error);
      throw error;
    }
  }

  /**
   * Parse a quantized mesh binary file
   */
  parse(buffer: ArrayBuffer): QuantizedMeshData {
    const bytes = new Uint8Array(buffer);
    const isGzip = bytes[0] === 0x1f && bytes[1] === 0x8b;
    const isZlib =
      bytes.length >= 2 &&
      (bytes[0] & 0x0f) === 8 &&
      (bytes[0] >> 4) <= 7 &&
      ((bytes[0] << 8) | bytes[1]) % 31 === 0;
    const data = isGzip || isZlib ? pako.inflate(bytes) : bytes;
    if (data.byteLength < 92) {
      throw new Error('Invalid quantized mesh: truncated header');
    }

    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    let offset = 0;

    // Header
    const centerX = view.getFloat64(offset, true);
    offset += 8;
    const centerY = view.getFloat64(offset, true);
    offset += 8;
    const centerZ = view.getFloat64(offset, true);
    offset += 8;
    const minimumHeight = view.getFloat32(offset, true);
    offset += 4;
    const maximumHeight = view.getFloat32(offset, true);
    offset += 4;
    const boundingSphereCenterX = view.getFloat64(offset, true);
    offset += 8;
    const boundingSphereCenterY = view.getFloat64(offset, true);
    offset += 8;
    const boundingSphereCenterZ = view.getFloat64(offset, true);
    offset += 8;
    const boundingSphereRadius = view.getFloat64(offset, true);
    offset += 8;
    const horizonOcclusionPointX = view.getFloat64(offset, true);
    offset += 8;
    const horizonOcclusionPointY = view.getFloat64(offset, true);
    offset += 8;
    const horizonOcclusionPointZ = view.getFloat64(offset, true);
    offset += 8;
    const header = {
      centerX,
      centerY,
      centerZ,
      minimumHeight,
      maximumHeight,
      boundingSphereCenterX,
      boundingSphereCenterY,
      boundingSphereCenterZ,
      boundingSphereRadius,
      horizonOcclusionPointX,
      horizonOcclusionPointY,
      horizonOcclusionPointZ,
    };

    // Vertex data
    const vertexCount = view.getUint32(offset, true);
    offset += 4;
    if (vertexCount > Math.floor((data.byteLength - offset) / 6)) {
      throw new Error('Invalid quantized mesh: vertexCount exceeds buffer capacity');
    }
    if (!Number.isFinite(minimumHeight) || !Number.isFinite(maximumHeight)) {
      throw new Error('Invalid quantized mesh: non-finite height range');
    }

    const u = new Uint16Array(vertexCount);
    const v = new Uint16Array(vertexCount);
    const height = new Uint16Array(vertexCount);

    // Read zigzag encoded deltas and decode
    const uDecoded = this.decodeZigZag(data, offset, vertexCount);
    offset += vertexCount * 2;
    const vDecoded = this.decodeZigZag(data, offset, vertexCount);
    offset += vertexCount * 2;
    const heightDecoded = this.decodeZigZag(data, offset, vertexCount);
    offset += vertexCount * 2;

    // Convert deltas to absolute values
    for (let i = 0; i < vertexCount; i++) {
      u[i] = uDecoded[i];
      v[i] = vDecoded[i];
      height[i] = heightDecoded[i];
    }

    // Triangle indices.
    // Per the quantized-mesh spec, the index data is byte-aligned: 2 bytes for
    // 16-bit indices, 4 bytes for 32-bit indices. Skip over any padding that was
    // added after the vertex data before reading the triangle count, otherwise
    // the read is misaligned and produces a garbage (huge) triangle count.
    const indexSize = vertexCount > 64 * 1024 ? 4 : 2;
    if (offset % indexSize !== 0) {
      offset += indexSize - (offset % indexSize);
    }

    const triangleCount = view.getUint32(offset, true);
    offset += 4;

    // Guard against corrupt/misaligned data: a valid triangle index buffer can
    // never exceed the remaining bytes in the tile. Bail out instead of
    // attempting a multi-gigabyte allocation.
    const remainingBytes = data.byteLength - offset;
    const maxTriangles = Math.floor(remainingBytes / (indexSize * 3));
    if (triangleCount < 0 || triangleCount > maxTriangles) {
      throw new Error(
        `Invalid quantized mesh: triangleCount ${triangleCount} exceeds buffer capacity ${maxTriangles}`,
      );
    }

    const indices = new Uint32Array(triangleCount * 3);

    for (let i = 0; i < triangleCount * 3; i++) {
      if (indexSize === 2) {
        indices[i] = view.getUint16(offset, true);
        offset += 2;
      } else {
        indices[i] = view.getUint32(offset, true);
        offset += 4;
      }
    }

    // Decode high water mark (indices are delta encoded)
    const highWaterMark = this.decodeIndices(indices);
    for (const index of highWaterMark) {
      if (index >= vertexCount) {
        throw new Error('Invalid quantized mesh: triangle index exceeds vertex count');
      }
    }

    // Edge indices (for tile stitching) - optional
    const edges = this.readEdgeIndices(view, offset, indexSize);

    return {
      header,
      vertexCount,
      u,
      v,
      height,
      indices: highWaterMark,
      triangleCount,
      edges,
    };
  }

  /**
   * Decode zigzag encoded delta values
   */
  decodeZigZag(data: Uint8Array, offset: number, count: number): Uint16Array {
    const result = new Uint16Array(count);
    const view = new DataView(data.buffer, data.byteOffset + offset, count * 2);

    let value = 0;
    for (let i = 0; i < count; i++) {
      const delta = view.getUint16(i * 2, true);
      // Zigzag decode
      const decoded = (delta >> 1) ^ -(delta & 1);
      value += decoded;
      if (value < 0 || value > 32767) {
        throw new Error('Invalid quantized mesh: vertex coordinate outside quantized range');
      }
      result[i] = value;
    }

    return result;
  }

  /**
   * Decode high water mark indices
   */
  decodeIndices(indices: Uint32Array): Uint32Array {
    let highest = 0;
    for (let i = 0; i < indices.length; i++) {
      const code = indices[i];
      indices[i] = highest - code;
      if (code === 0) {
        highest++;
      }
    }
    return indices;
  }

  /**
   * Read edge indices for tile stitching
   */
  readEdgeIndices(view: DataView, offset: number, indexSize: number): QuantizedMeshData['edges'] {
    const edges: QuantizedMeshData['edges'] = {
      west: [],
      south: [],
      east: [],
      north: [],
    };

    try {
      // West edge indices
      const westCount = view.getUint32(offset, true);
      offset += 4;
      for (let i = 0; i < westCount; i++) {
        const idx = indexSize === 2 ? view.getUint16(offset, true) : view.getUint32(offset, true);
        edges.west.push(idx);
        offset += indexSize;
      }

      // South edge indices
      const southCount = view.getUint32(offset, true);
      offset += 4;
      for (let i = 0; i < southCount; i++) {
        const idx = indexSize === 2 ? view.getUint16(offset, true) : view.getUint32(offset, true);
        edges.south.push(idx);
        offset += indexSize;
      }

      // East edge indices
      const eastCount = view.getUint32(offset, true);
      offset += 4;
      for (let i = 0; i < eastCount; i++) {
        const idx = indexSize === 2 ? view.getUint16(offset, true) : view.getUint32(offset, true);
        edges.east.push(idx);
        offset += indexSize;
      }

      // North edge indices
      const northCount = view.getUint32(offset, true);
      offset += 4;
      for (let i = 0; i < northCount; i++) {
        const idx = indexSize === 2 ? view.getUint16(offset, true) : view.getUint32(offset, true);
        edges.north.push(idx);
        offset += indexSize;
      }
    } catch (e) {
      // Edge indices might not be present in all files
      console.debug('Edge indices not available or error reading them', e);
    }

    return edges;
  }

  /**
   * Create a Three.js mesh from parsed terrain data
   */
  createMesh(terrainData: QuantizedMeshData, tileBounds: GeoBounds): THREE.Mesh {
    const { header, vertexCount, u, v, height, indices } = terrainData;

    // Create geometry
    const geometry = new THREE.BufferGeometry();

    // Convert quantized positions to actual coordinates
    const positions = new Float32Array(vertexCount * 3);
    const originalPositions = new Float32Array(vertexCount * 3);
    const normals = new Float32Array(vertexCount * 3);
    const uvs = new Float32Array(vertexCount * 2);

    const lonRange = tileBounds.east - tileBounds.west;
    const latRange = tileBounds.north - tileBounds.south;
    const heightRange = header.maximumHeight - header.minimumHeight;

    for (let i = 0; i < vertexCount; i++) {
      // Dequantize positions
      const uNorm = u[i] / 32767.0;
      const vNorm = v[i] / 32767.0;
      const hNorm = height[i] / 32767.0;

      // Convert to geographic coordinates
      const lon = tileBounds.west + uNorm * lonRange;
      const lat = tileBounds.south + vNorm * latRange;
      const h = header.minimumHeight + hNorm * heightRange;

      // Store positions (will be transformed to LV95 later)
      positions[i * 3] = lon;
      positions[i * 3 + 1] = lat;
      positions[i * 3 + 2] = h;

      // Store original WGS84 positions for texture UV calculation
      originalPositions[i * 3] = lon;
      originalPositions[i * 3 + 1] = lat;
      originalPositions[i * 3 + 2] = h;

      // UVs
      uvs[i * 2] = uNorm;
      uvs[i * 2 + 1] = vNorm;

      // Normals will be computed later
      normals[i * 3] = 0;
      normals[i * 3 + 1] = 0;
      normals[i * 3 + 2] = 1;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));

    // Compute normals
    geometry.computeVertexNormals();

    // Create material
    const material = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.9,
      metalness: 0.0,
      flatShading: false,
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData.terrainData = terrainData;
    mesh.userData.tileBounds = tileBounds;
    mesh.userData.originalPositions = originalPositions;

    return mesh;
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache.clear();
    this.rawCache.clear();
  }

  /**
   * Get cache size
   */
  getCacheSize(): number {
    return this.cache.size;
  }
}
